import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Play, Compass, Layers, Calendar, MapPin, Award, CheckCircle2 } from 'lucide-react';
import { Project } from '../data/portfolioData';

interface ProjectModalProps {
  project: Project | null;
  onClose: () => void;
}

export const ProjectModal: React.FC<ProjectModalProps> = ({ project, onClose }) => {
  const [activeMediaTab, setActiveMediaTab] = useState<'photos' | 'video' | 'blueprint'>('photos');
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(0);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (project) {
      document.body.style.overflow = 'hidden';
      window.addEventListener('keydown', handleKeyDown);
      setActiveMediaTab('photos');
      setSelectedPhotoIndex(0);
    }
    return () => {
      document.body.style.overflow = 'unset';
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [project, onClose]);

  if (!project) return null;

  const allPhotos = [project.heroImage, ...project.gallery];

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-end overflow-y-auto">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 bg-[#0A0B0D]/85 backdrop-blur-md cursor-pointer"
        />

        {/* Modal / Slide-over Container */}
        <motion.div
          initial={{ x: '100%' }}
          animate={{ x: 0 }}
          exit={{ x: '100%' }}
          transition={{ type: 'spring', damping: 28, stiffness: 260 }}
          className="relative w-full max-w-5xl h-full min-h-screen bg-[#0E1013] border-l border-white/10 shadow-2xl z-10 flex flex-col justify-between overflow-y-auto"
        >
          {/* Top Fixed Header */}
          <div className="sticky top-0 z-30 bg-[#0E1013]/90 backdrop-blur-md px-6 md:px-10 py-5 border-b border-white/10 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <span className="text-xs font-mono text-arch-accent tracking-widest uppercase">
                PROJECT DOSSIER // {project.id}
              </span>
              <span className="hidden sm:inline-block w-1.5 h-1.5 rounded-full bg-white/20" />
              <span className="hidden sm:inline-block text-xs font-mono text-neutral-400">
                {project.category}
              </span>
            </div>

            <button
              onClick={onClose}
              className="p-2 rounded-full bg-white/5 hover:bg-white/10 text-neutral-300 hover:text-white border border-white/10 transition-colors"
              aria-label="Close Project Modal"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Modal Body */}
          <div className="p-6 md:p-10 space-y-12">
            {/* Title & Metadata Overview */}
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-3 text-xs font-mono text-arch-muted uppercase">
                <span className="flex items-center gap-1">
                  <MapPin className="w-3.5 h-3.5 text-arch-accent" /> {project.location}
                </span>
                <span>•</span>
                <span className="flex items-center gap-1">
                  <Calendar className="w-3.5 h-3.5 text-arch-accent" /> Completed {project.year}
                </span>
                <span>•</span>
                <span className="flex items-center gap-1">
                  <Layers className="w-3.5 h-3.5 text-arch-accent" /> {project.area}
                </span>
                <span>•</span>
                <span className="text-emerald-400 font-medium">{project.status}</span>
              </div>

              <h2 className="text-3xl sm:text-5xl font-serif text-white font-semibold tracking-tight">
                {project.title}
              </h2>
            </div>

            {/* Media Navigation Tabs */}
            <div className="flex items-center gap-3 border-b border-white/10 pb-4">
              <button
                onClick={() => setActiveMediaTab('photos')}
                className={`px-4 py-2 rounded text-xs font-mono uppercase tracking-wider transition-colors ${
                  activeMediaTab === 'photos'
                    ? 'bg-arch-accent text-[#0A0B0D] font-semibold'
                    : 'text-neutral-400 hover:text-white hover:bg-white/5'
                }`}
              >
                Photography Gallery ({allPhotos.length})
              </button>

              {project.videoUrl && (
                <button
                  onClick={() => setActiveMediaTab('video')}
                  className={`px-4 py-2 rounded text-xs font-mono uppercase tracking-wider transition-colors flex items-center gap-1.5 ${
                    activeMediaTab === 'video'
                      ? 'bg-arch-accent text-[#0A0B0D] font-semibold'
                      : 'text-neutral-400 hover:text-white hover:bg-white/5'
                  }`}
                >
                  <Play className="w-3.5 h-3.5 fill-current" /> Video Walkthrough
                </button>
              )}

              {project.blueprintImage && (
                <button
                  onClick={() => setActiveMediaTab('blueprint')}
                  className={`px-4 py-2 rounded text-xs font-mono uppercase tracking-wider transition-colors ${
                    activeMediaTab === 'blueprint'
                      ? 'bg-arch-cyan text-[#0A0B0D] font-semibold'
                      : 'text-neutral-400 hover:text-white hover:bg-white/5'
                  }`}
                >
                  Blueprint CAD Drawings
                </button>
              )}
            </div>

            {/* Active Media Display Area */}
            <div className="space-y-4">
              {activeMediaTab === 'photos' && (
                <div className="space-y-4">
                  {/* Primary Large Photo Frame */}
                  <div className="relative aspect-[16/10] rounded-sm overflow-hidden bg-black border border-white/10">
                    <img
                      src={allPhotos[selectedPhotoIndex]}
                      alt={`${project.title} - View ${selectedPhotoIndex + 1}`}
                      className="w-full h-full object-cover transition-all duration-500"
                    />
                    <div className="absolute bottom-3 right-3 px-3 py-1 rounded bg-[#0A0B0D]/80 backdrop-blur text-[10px] font-mono text-neutral-300 border border-white/10">
                      FIG. 0{selectedPhotoIndex + 1} OF 0{allPhotos.length}
                    </div>
                  </div>

                  {/* Thumbnail Row */}
                  <div className="flex items-center gap-3 overflow-x-auto pb-2">
                    {allPhotos.map((photo, idx) => (
                      <button
                        key={idx}
                        onClick={() => setSelectedPhotoIndex(idx)}
                        className={`relative w-24 h-16 rounded-sm overflow-hidden flex-shrink-0 border transition-all ${
                          selectedPhotoIndex === idx
                            ? 'border-arch-accent ring-2 ring-arch-accent/30'
                            : 'border-white/10 opacity-60 hover:opacity-100'
                        }`}
                      >
                        <img src={photo} alt="thumbnail" className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {activeMediaTab === 'video' && project.videoUrl && (
                <div className="aspect-[16/9] rounded-sm overflow-hidden bg-black border border-white/10 relative">
                  <video
                    src={project.videoUrl}
                    controls
                    autoPlay
                    loop
                    className="w-full h-full object-cover"
                  >
                    Your browser does not support HTML5 video.
                  </video>
                </div>
              )}

              {activeMediaTab === 'blueprint' && project.blueprintImage && (
                <div className="space-y-2">
                  <div className="relative aspect-[16/10] rounded-sm overflow-hidden bg-black border border-white/10">
                    <img
                      src={project.blueprintImage}
                      alt="Architectural Blueprint Drawing"
                      className="w-full h-full object-cover filter invert contrast-125"
                    />
                    <div className="absolute top-3 left-3 px-3 py-1 rounded bg-[#0A0B0D]/80 backdrop-blur text-[10px] font-mono text-arch-cyan border border-arch-cyan/30">
                      CAD SCHEMATIC // PLAN & SECTION 1:100
                    </div>
                  </div>
                  <div className="text-[10px] font-mono text-arch-muted">
                    NOTE: Vector drawings rendered for dimension verification and zoning compliance.
                  </div>
                </div>
              )}
            </div>

            {/* Narrative & Concept Statement */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 pt-4">
              <div className="lg:col-span-7 space-y-6">
                <div className="text-xs font-mono text-arch-accent uppercase tracking-widest flex items-center gap-2">
                  <Compass className="w-3.5 h-3.5" />
                  <span>Design Concept & Architectural Narrative</span>
                </div>
                <p className="text-sm sm:text-base text-neutral-300 font-light leading-relaxed">
                  {project.narrative}
                </p>
                {project.specs.awards && project.specs.awards.length > 0 && (
                  <div className="p-4 rounded bg-arch-accent/10 border border-arch-accent/20 flex items-start gap-3">
                    <Award className="w-5 h-5 text-arch-accent flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="text-xs font-mono text-arch-accent uppercase font-medium">
                        Citations & Recognition
                      </div>
                      <div className="text-xs text-neutral-300 font-light mt-0.5">
                        {project.specs.awards.join(', ')}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Technical Specifications Sheet */}
              <div className="lg:col-span-5 bg-white/[0.03] border border-white/10 p-6 rounded-sm space-y-6">
                <div className="text-xs font-mono text-white uppercase tracking-widest border-b border-white/10 pb-3 flex items-center justify-between">
                  <span>Technical Data Sheet</span>
                  <span className="text-[10px] text-arch-muted font-mono">SPEC-REV-4</span>
                </div>

                <dl className="space-y-4 text-xs font-mono">
                  <div>
                    <dt className="text-arch-muted uppercase text-[10px]">Typology</dt>
                    <dd className="text-white font-medium mt-0.5">{project.specs.typology}</dd>
                  </div>
                  <div>
                    <dt className="text-arch-muted uppercase text-[10px]">Structural System</dt>
                    <dd className="text-white mt-0.5">{project.specs.structuralSystem}</dd>
                  </div>
                  <div>
                    <dt className="text-arch-muted uppercase text-[10px]">Primary Materials</dt>
                    <dd className="text-neutral-300 mt-1 flex flex-wrap gap-1.5">
                      {project.specs.materials.map((m) => (
                        <span key={m} className="px-2 py-0.5 rounded bg-white/5 border border-white/10 text-[11px]">
                          {m}
                        </span>
                      ))}
                    </dd>
                  </div>
                  {project.specs.siteArea && (
                    <div>
                      <dt className="text-arch-muted uppercase text-[10px]">Site Plot Area</dt>
                      <dd className="text-white mt-0.5">{project.specs.siteArea}</dd>
                    </div>
                  )}
                  <div>
                    <dt className="text-arch-muted uppercase text-[10px]">Lead Architect</dt>
                    <dd className="text-arch-accent mt-0.5 flex items-center gap-1.5">
                      <CheckCircle2 className="w-3 h-3" />
                      {project.specs.leadArchitect || 'Arch. Dominic "Hutch" Glarino'}
                    </dd>
                  </div>
                </dl>
              </div>
            </div>
          </div>

          {/* Bottom Action Bar */}
          <div className="sticky bottom-0 bg-[#0E1013]/95 backdrop-blur px-6 md:px-10 py-4 border-t border-white/10 flex flex-wrap items-center justify-between gap-4">
            <div className="text-xs font-mono text-neutral-400">
              Interested in a project with similar typology or scale?
            </div>
            <a
              href="#inquire"
              onClick={onClose}
              className="px-6 py-2.5 rounded-sm bg-arch-accent text-[#0A0B0D] text-xs font-mono uppercase tracking-widest font-semibold hover:bg-white transition-colors"
            >
              Inquire About Similar Commission →
            </a>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};
